import { GameConfig } from "../config/GameConfig";
import { GameNavigator } from "../core/Navigation";
import { AdFarmConfig } from "../integration/AdFarmConfig";
import { AdFarmSession } from "../integration/AdFarmSession";
import { MobileRole } from "../integration/AdFarmTypes";
import { Theme } from "../ui/Theme";
import { UIFactory } from "../ui/UIFactory";
import { BaseScreen } from "./BaseScreen";

export class AuthScreen extends BaseScreen {
    private readonly phone = this.createInput("请输入手机号", 11, Laya.Input.TYPE_NUMBER);
    private readonly code = this.createInput("请输入短信验证码", 6, Laya.Input.TYPE_NUMBER);
    private readonly invite = this.createInput("请输入真实邀请码", 32, Laya.Input.TYPE_TEXT);
    private readonly inviteLocked: boolean;
    private readonly roleUser: Laya.Box;
    private readonly roleAgent: Laya.Box;
    private readonly sendCodeButton: Laya.Box;
    private readonly privacyLabel: Laya.Label;
    private role: MobileRole = "user";
    private privacyAccepted = false;
    private sending = false;
    private countdown = 0;

    constructor(nav: GameNavigator) {
        super(nav);
        const inviteCode = this.readInviteCode();
        this.inviteLocked = !!inviteCode;
        this.invite.text = inviteCode;
        this.buildBackground();
        this.roleUser = this.roleButton("普通用户", "user", 86);
        this.roleAgent = this.roleButton("二级代理", "secondaryAgent", 383);
        this.addChild(this.roleUser);
        this.addChild(this.roleAgent);
        if (this.inviteLocked) {
            this.roleAgent.visible = false;
            this.invite.editable = false;
        }

        this.addInputRow("手机号", this.phone, 470);
        this.addInputRow("验证码", this.code, 590, 385);
        this.sendCodeButton = UIFactory.button("获取验证码", 190, 72, () => this.sendCode(), "gold", 25);
        this.sendCodeButton.pos(474, 590);
        this.addChild(this.sendCodeButton);
        this.addInputRow("邀请码", this.invite, 710);

        this.privacyLabel = UIFactory.label("□ 我已阅读并同意《隐私政策》和《用户协议》", 23, Theme.ink, 600, 58, false);
        this.privacyLabel.align = "left";
        this.privacyLabel.pos(82, 820);
        this.privacyLabel.mouseEnabled = true;
        this.privacyLabel.on(Laya.Event.CLICK, this, this.togglePrivacy);
        this.addChild(this.privacyLabel);
        if (AdFarmConfig.RISK_DISABLED) {
            this.privacyAccepted = true;
            this.privacyLabel.text = "☑ 本地测试模式（风控已关闭）";
            this.privacyLabel.mouseEnabled = false;
        }

        const login = UIFactory.button("登录", 270, 82, () => this.submit(false), "red", 34);
        login.pos(82, 910);
        this.addChild(login);
        const register = UIFactory.button("注册并进入游戏", 270, 82, () => this.submit(true), "green", 29);
        register.pos(398, 910);
        this.addChild(register);

        const hint = UIFactory.label(
            this.inviteLocked ? "已从邀请链接进入，身份固定为普通用户" : "注册必须填写有效邀请码；登录无需填写邀请码",
            21,
            "#8C4E32",
            620,
            62,
            false,
        );
        hint.pos(65, 1025);
        hint.wordWrap = true;
        this.addChild(hint);
        this.checkHealth();
    }

    destroy(destroyChild = true): void {
        Laya.timer.clearAll(this);
        super.destroy(destroyChild);
    }

    private buildBackground(): void {
        this.graphics.drawRect(0, 0, this.width, this.height, "#082F50");
        this.graphics.drawCircle(80, 140, 190, "#3FAFD5");
        this.graphics.drawCircle(680, 120, 220, "#2C84B5");
        this.graphics.drawRect(0, 1080, this.width, this.height - 1080, "#E84930");
        const title = UIFactory.label("成语红包王", 66, "#FFE7A1", 650, 92, true);
        title.pos(50, 128);
        title.stroke = 7;
        title.strokeColor = "#8F271E";
        this.addChild(title);
        const subtitle = UIFactory.label("答成语 · 赢金币 · 看广告领红包", 27, "#FFF4D4", 620, 56, true);
        subtitle.pos(65, 220);
        this.addChild(subtitle);
        const panel = UIFactory.panel(650, 760, "手机号登录 / 注册");
        panel.pos(50, 330);
        this.addChild(panel);
        const api = UIFactory.label(`服务：${AdFarmConfig.API_BASE_URL}`, 18, "#FFE7C3", 680, 40, false);
        api.pos(35, 1110);
        this.addChild(api);
    }

    private roleButton(text: string, role: MobileRole, x: number): Laya.Box {
        const box = UIFactory.button(text, 280, 70, () => this.setRole(role), role === this.role ? "red" : "cream", 29);
        box.pos(x, 375);
        box.name = `role_${role}`;
        return box;
    }

    private setRole(role: MobileRole): void {
        if (this.inviteLocked && role !== "user") {
            this.nav.toast("邀请入口只能注册普通用户");
            return;
        }
        this.role = role;
        UIFactory.paintButton(this.roleUser, 280, 70, role === "user" ? "red" : "cream");
        UIFactory.paintButton(this.roleAgent, 280, 70, role === "secondaryAgent" ? "red" : "cream");
        (this.roleUser.getChildAt(0) as Laya.Label).color = role === "user" ? "#FFF5C7" : Theme.ink;
        (this.roleAgent.getChildAt(0) as Laya.Label).color = role === "secondaryAgent" ? "#FFF5C7" : Theme.ink;
    }

    private createInput(prompt: string, maxChars: number, type: string): Laya.Input {
        const input = new Laya.Input();
        input.prompt = prompt;
        input.promptColor = "#B18A6B";
        input.color = Theme.ink;
        input.font = "Microsoft YaHei";
        input.fontSize = 27;
        input.maxChars = maxChars;
        input.type = type;
        input.padding = "0,18,0,18";
        input.valign = "middle";
        return input;
    }

    private addInputRow(labelText: string, input: Laya.Input, y: number, width = 580): void {
        const label = UIFactory.label(labelText, 23, Theme.ink, 116, 62, true);
        label.align = "left";
        label.pos(88, y + 6);
        this.addChild(label);
        const x = 190;
        const inputWidth = width - 112;
        const box = new Laya.Box();
        box.pos(x, y);
        box.width = inputWidth;
        box.height = 72;
        UIFactory.roundRect(box, 0, 0, inputWidth, 72, 16, "#FFF9E8", "#D38A43", 3);
        this.addChild(box);
        input.pos(x + 4, y + 4);
        input.width = inputWidth - 8;
        input.height = 64;
        this.addChild(input);
    }

    private async checkHealth(): Promise<void> {
        try {
            await AdFarmSession.instance.api.health();
        } catch (error) {
            this.nav.toast(this.errorMessage(error));
        }
    }

    private togglePrivacy(): void {
        if (AdFarmConfig.RISK_DISABLED) return;
        this.privacyAccepted = !this.privacyAccepted;
        this.privacyLabel.text = `${this.privacyAccepted ? "☑" : "□"} 我已阅读并同意《隐私政策》和《用户协议》`;
    }

    private async ensurePrivacy(): Promise<void> {
        if (!this.privacyAccepted && !AdFarmSession.instance.risk.hasConsent()) throw new Error("请先阅读并同意隐私政策与用户协议");
        if (!AdFarmSession.instance.risk.hasConsent()) await AdFarmSession.instance.risk.acceptPrivacy();
    }

    private async sendCode(): Promise<void> {
        if (this.sending || this.countdown > 0) return;
        this.sending = true;
        try {
            await this.ensurePrivacy();
            const result = await AdFarmSession.instance.sendCode(this.phone.text.trim()) as { debugCode?: string } | null;
            this.countdown = 60;
            this.updateCountdown();
            Laya.timer.loop(1000, this, this.updateCountdown);
            this.nav.toast(result?.debugCode ? `本地测试验证码：${result.debugCode}` : "验证码已发送，请注意查收");
        } catch (error) {
            this.nav.toast(this.errorMessage(error));
        } finally {
            this.sending = false;
        }
    }

    private updateCountdown(): void {
        const label = this.sendCodeButton.getChildAt(0) as Laya.Label;
        if (this.countdown <= 0) {
            Laya.timer.clear(this, this.updateCountdown);
            label.text = "重新获取";
            return;
        }
        label.text = `${this.countdown--} 秒`;
    }

    private async submit(register: boolean): Promise<void> {
        try {
            await this.ensurePrivacy();
            const session = AdFarmSession.instance;
            if (register) {
                await session.register({
                    phone: this.phone.text.trim(),
                    code: this.code.text.trim(),
                    inviteCode: this.invite.text.trim(),
                    role: this.role,
                    inviteEntryLockedToUser: this.inviteLocked,
                });
            } else {
                await session.login({ phone: this.phone.text.trim(), code: this.code.text.trim(), role: this.role });
            }
            this.nav.toast(register ? "注册成功" : "登录成功");
            this.nav.navigate("home");
        } catch (error) {
            this.nav.toast(this.errorMessage(error));
        }
    }

    private readInviteCode(): string {
        try {
            const params = new URLSearchParams(globalThis.location?.search || "");
            return (params.get("code") || params.get("inviteCode") || "").trim();
        } catch (_error) {
            return "";
        }
    }

    private errorMessage(error: unknown): string {
        return error instanceof Error ? error.message : "操作失败，请稍后重试";
    }
}
